import React from "react";
import AuthorizedPage from "../../models/base/AuthorizedPage";
import {SynchronizationDataType, SynchronizationType} from "@/models/Enums";
import {SynchronizationRequest} from "@/models/server/requests/SynchronizationRequest";
import {ch} from "@renta-apps/athenaeum-react-common";
import {Utility} from "@renta-apps/athenaeum-toolkit";
import {Button, ButtonContainer, ButtonType, Checkbox, DateInput, Dropdown, Form, Inline, InlineType, PageContainer, PageHeader, PageRow, TextInput, TwoColumns} from "@renta-apps/athenaeum-react-components";
import EndpointPaths from "@/common/EndpointPaths";
import EnumProvider from "@/providers/EnumProvider";
import Localizer from "@/localization/Localizer";

interface ISynchronizationPageProps  {
}

interface ISynchronizationPageState {
    synchronizationDataType: SynchronizationDataType,
    synchronizationType: SynchronizationType,
    syncFrom: Date | null,
    customerExternalId: string | null,
    includePrivateCustomers: boolean
}

export default class SynchronizationPage extends AuthorizedPage<ISynchronizationPageProps, ISynchronizationPageState> {

    state: ISynchronizationPageState = {
        synchronizationDataType: SynchronizationDataType.OrganizationContracts,
        synchronizationType: SynchronizationType.All,
        syncFrom: null,
        customerExternalId: null,
        includePrivateCustomers: false
    };

    private readonly _syncFromRef: React.RefObject<DateInput> = React.createRef();

    private get synchronizationDataType(): SynchronizationDataType {
        return this.state.synchronizationDataType;
    }

    private get synchronizationType(): SynchronizationType {
        return this.state.synchronizationType;
    }

    private get syncFrom(): Date | null {
        return this.state.syncFrom;
    }

    private get customerExternalId(): string | null {
        return this.state.customerExternalId;
    }

    private get includePrivateCustomers(): boolean {
        return this.state.includePrivateCustomers;
    }

    private get canRun(): boolean {
        return ((this.synchronizationType == SynchronizationType.All) ||
            (this.synchronizationType == SynchronizationType.SyncFrom && this.syncFrom != null) ||
            (this.synchronizationType == SynchronizationType.CustomerSpecific && this.customerExternalId != null));
    }

    private async clearStateAsync(): Promise<void> {
        await this.setState(
            {
                syncFrom: null,
                customerExternalId: null,
                includePrivateCustomers: false
            }
        );
    }

    private async onChangeSynchronizationDataTypeAsync(synchronizationDataType: SynchronizationDataType): Promise<void> {
        await this.setState({synchronizationDataType: synchronizationDataType});
    }

    private async onChangeSynchronizationTypeAsync(synchronizationType: SynchronizationType): Promise<void> {
        await this.setState({synchronizationType: synchronizationType});
        await this.clearStateAsync();
    }

    private async onChangeCustomerExternalIdAsync(customerExternalId: string | null): Promise<void> {
        if (customerExternalId != null && customerExternalId.length > 0) {
            await this.setState({customerExternalId});
        } else {
            await this.setState({customerExternalId: null});
        }
    }

    private async onChangePrivateCustomersOnlyAsync(privateCustomersOnly: boolean): Promise<void> {
        await this.setState({includePrivateCustomers: privateCustomersOnly});
    }

    private async onChangeSyncFromAsync(syncFrom: Date | null): Promise<void> {
        await this.setState({syncFrom});
    }

    private async handleSubmitAsync(): Promise<void> {
        const request = new SynchronizationRequest();
        request.synchronizationDataType = this.synchronizationDataType;
        request.synchronizationType = this.synchronizationType;
        request.includePrivateCustomers = this.includePrivateCustomers;

        switch (this.synchronizationType) {
            case SynchronizationType.All:
                break;
            case SynchronizationType.SyncFrom:
                request.syncFrom = this.syncFrom;
                break;
            case SynchronizationType.CustomerSpecific:
                request.customerExternalId = this.customerExternalId;
                break;
        }

        const confirmed: boolean = await ch.confirmAsync(this.getConfirmationMessage());

        if (!confirmed) {
            return;
        }

        await this.postAsync(EndpointPaths.SynchronizationPaths.SynchronizeData, request);

        await ch.alertMessageAsync(Localizer.synchronizationPageAlertMessageSubmit, true);
    }

    private getConfirmationMessage(): string {
        switch (this.synchronizationType) {
            case SynchronizationType.All:
                return Localizer.synchronizationPageConfirmationMessageSyncAll.format(this.synchronizationDataType);
            case SynchronizationType.SyncFrom:
                return Localizer.synchronizationPageConfirmationMessageSyncFrom.format(this.synchronizationDataType, this.syncFrom?.format("D"));
            case SynchronizationType.CustomerSpecific:
                return Localizer.synchronizationPageConfirmationMessageSyncCustomerSpecific.format(this.synchronizationDataType, this.customerExternalId);
        }
    }

    private getSynchronizationDescription(): string {
        switch (this.synchronizationType) {
            case SynchronizationType.All:
                return Localizer.synchronizationPageSynchronizationDescriptionAll.format(this.synchronizationDataType);
            case SynchronizationType.SyncFrom:
                return Localizer.synchronizationPageSynchronizationDescriptionSyncFrom.format(this.synchronizationDataType);
            case SynchronizationType.CustomerSpecific:
                return Localizer.synchronizationPageSynchronizationDescriptionCustomerSpecific.format(this.synchronizationDataType);
        }
    }

    public render(): React.ReactNode {
        return (
            <PageContainer>

                <PageHeader title={Localizer.synchronizationPageTitle}/>

                <PageRow>

                    <div className="col">

                        <Form id="form"
                              onSubmit={async () => await this.handleSubmitAsync()}
                        >

                            <TwoColumns>

                                <Dropdown required
                                          id="synchronizationDataType"
                                          label={Localizer.synchronizationPageDropdownSelectSynchronizationDataType}
                                          items={EnumProvider.getSynchronizationDataTypeItems()}
                                          selectedItem={EnumProvider.getSynchronizationDataTypeItem(this.synchronizationDataType)}
                                          onChange={async (_, item) => this.onChangeSynchronizationDataTypeAsync(parseInt(item!.value))}
                                />

                                <Dropdown required
                                          id="synchronizationType"
                                          label={Localizer.synchronizationPageDropdownSelectSynchronizationType}
                                          items={EnumProvider.getSynchronizationTypeItems()}
                                          selectedItem={EnumProvider.getSynchronizationTypeItem(this.synchronizationType)}
                                          onChange={async (_, item) => this.onChangeSynchronizationTypeAsync(parseInt(item!.value))}
                                />

                            </TwoColumns>

                            <div className={"w-100"}>
                                <span className={"d-block"}>{Localizer.synchronizationPageSynchronizationDescription + ":"}</span>
                                <span>{this.getSynchronizationDescription()}</span>

                                <hr/>

                            </div>

                            <TwoColumns>

                                {
                                    (this.synchronizationDataType == SynchronizationDataType.OrganizationContracts && ch.isNorway) &&
                                    (
                                        <Checkbox inline
                                                  id="privateCustomersOnly"
                                                  inlineType={InlineType.Right}
                                                  label={Localizer.synchronizationPageCheckboxIncludePrivateCustomers}
                                                  value={this.includePrivateCustomers}
                                                  onChange={async (_, value) => await this.onChangePrivateCustomersOnlyAsync(value)}
                                        />
                                    )
                                }

                                {
                                    ((this.synchronizationType == SynchronizationType.CustomerSpecific) && (!ch.isNorway || this.synchronizationDataType == SynchronizationDataType.ConstructionSites)) &&
                                    (
                                        <TextInput trim required
                                                   id="customerExternalId"
                                                   placeholder={Localizer.synchronizationPageTextInputPlaceholderCustomerNumber}
                                                   label={Localizer.synchronizationPageTextInputCustomerNumber}
                                                   value={this.customerExternalId}
                                                   onChange={async (_, value) => this.onChangeCustomerExternalIdAsync(value)}
                                        />
                                    )
                                }

                                {
                                    (this.synchronizationType == SynchronizationType.SyncFrom) &&
                                    (
                                        <Inline>

                                            <DateInput clearButton required
                                                       id="syncFrom"
                                                       ref={this._syncFromRef}
                                                       label={Localizer.synchronizationPageDateInputSyncFrom}
                                                       value={(this.syncFrom) ? Utility.toUtc(this.syncFrom) : null}
                                                       maxDate={Utility.now()}
                                                       onChange={async (value) => this.onChangeSyncFromAsync(value)}
                                            />

                                        </Inline>
                                    )
                                }

                            </TwoColumns>

                            <ButtonContainer>

                                <Button submit
                                        id={"runSynchronization"}
                                        label={Localizer.synchronizationPageButtonStartSynchronization}
                                        disabled={!this.canRun}
                                        type={ButtonType.Orange}
                                        icon={{name: "fa-play"}}
                                />

                            </ButtonContainer>

                        </Form>

                    </div>

                </PageRow>

            </PageContainer>
        );
    }
}